import React, { useEffect, useState, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Search, CheckCircle2, XCircle, AlertTriangle, Camera, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

export default function CheckIn() {
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const audioRef = useRef(null);

  const feedback = (kind) => {
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      const notes = kind === "success" ? [880, 1320] : kind === "warn" ? [660, 660] : [220, 160];
      notes.forEach((f, i) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = kind === "error" ? "square" : "sine";
        o.frequency.value = f;
        o.connect(g); g.connect(ctx.destination);
        const t = now + i * 0.14;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.13);
        o.start(t); o.stop(t + 0.14);
      });
    } catch (e) {}
    try {
      if (navigator.vibrate) navigator.vibrate(kind === "success" ? 200 : kind === "warn" ? [80, 40, 80] : [120, 60, 120]);
    } catch (e) {}
  };

  const feedbackFor = (result) =>
    feedback(result === "valid" || result === "checked_in" ? "success" : result === "already" ? "warn" : "error");

  useEffect(() => { api.get("/admin/checkin/events").then((r) => setEvents(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!event) return;
    loadStats();
    const id = setInterval(loadStats, 5000);
    return () => { clearInterval(id); stopScanner(); };
    /* eslint-disable-next-line */
  }, [event]);

  const loadStats = () => api.get(`/admin/checkin/${event.id}/stats`).then((r) => setStats(r.data)).catch(() => {});

  const search = async () => {
    if (!q) return;
    const { data } = await api.get(`/admin/checkin/${event.id}/search?q=${encodeURIComponent(q)}`);
    setResults(data);
  };

  const handleToken = async (token) => {
    try {
      const { data } = await api.post("/admin/checkin/scan", { token, event_id: event.id });
      setScanResult(data);
      feedbackFor(data.result);
    } catch (e) { setScanResult({ result: "invalid", message: "Error scanning." }); feedback("error"); }
  };

  const checkIn = async (ticketId, override = false) => {
    try {
      const { data } = await api.post(`/admin/checkin/ticket/${ticketId}/checkin`, { override });
      toast.success(data.message);
      setScanResult({ result: "checked_in", message: data.message, ticket: data.ticket });
      setResults((rs) => rs.map((r) => (r.id === ticketId ? data.ticket : r)));
      feedback("success");
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); feedback("error"); }
  };

  const startScanner = async () => {
    setScanning(true);
    setScanResult(null);
    await new Promise((r) => setTimeout(r, 100));
    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 250 }, async (decoded) => {
        await stopScanner();
        handleToken(decoded);
      }, () => {});
    } catch (e) { toast.error("Camera unavailable. Use search instead."); setScanning(false); }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); scannerRef.current.clear(); } catch (e) {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  if (!event) {
    return (
      <div>
        <h1 className="font-display text-3xl uppercase text-white mb-6">Check-In · Select Event</h1>
        <div className="space-y-3">
          {events.map((e) => (
            <button key={e.id} data-testid={`checkin-event-${e.id}`} onClick={() => setEvent(e)} className="w-full text-left rounded-xl border border-[#27272A] bg-[#18181B] p-4 hover:border-[#D4AF37]">
              <p className="font-semibold text-white">{e.name}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{e.date} · {e.venue?.name || "—"}</p>
            </button>
          ))}
          {events.length === 0 && <div className="rounded-xl border border-[#27272A] p-10 text-center text-zinc-500">No upcoming events to check in.</div>}
        </div>
      </div>
    );
  }

  const rc = scanResult?.result;

  return (
    <div className="max-w-md mx-auto">
      <button onClick={() => { setEvent(null); setScanResult(null); setResults([]); }} className="inline-flex items-center gap-1.5 text-xs font-oswald tracking-widest text-zinc-400 hover:text-white mb-3"><ArrowLeft size={14} /> CHANGE EVENT</button>
      <h1 className="font-display text-2xl uppercase text-white leading-none">{event.name}</h1>

      {stats && (
        <div className="mt-4 rounded-2xl border border-[#27272A] bg-[#18181B] p-5" data-testid="live-door-counter">
          <div className="flex items-center justify-between mb-3">
            <span className="font-oswald text-[11px] tracking-widest text-zinc-400 uppercase">Live Door Count</span>
            <span className="flex items-center gap-1.5 text-[10px] font-oswald tracking-widest text-emerald-400 uppercase">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
              Live
            </span>
          </div>
          <div className="flex items-end gap-2">
            <span data-testid="counter-checked-in" className="font-display text-6xl leading-none text-[#D4AF37]">{stats.checked_in}</span>
            <span className="font-display text-3xl leading-none text-zinc-500 mb-1">/ {stats.total}</span>
            <span className="ml-auto text-right"><span data-testid="counter-remaining" className="font-display text-4xl leading-none text-white block">{stats.remaining}</span><span className="text-[10px] text-zinc-500 uppercase">Remaining</span></span>
          </div>
          <div className="mt-4 h-2.5 w-full rounded-full bg-[#27272A] overflow-hidden">
            <div className="h-full bg-[#D4AF37] transition-all duration-500" style={{ width: `${stats.total ? Math.round((stats.checked_in / stats.total) * 100) : 0}%` }} />
          </div>
          <p className="mt-2 text-[11px] text-zinc-500">{stats.checked_in} checked in · {stats.remaining} still to arrive · updates every few seconds</p>
        </div>
      )}

      {/* Scan result banner */}
      {scanResult && (
        <div data-testid="scan-result" className={`mt-4 rounded-xl p-5 text-center ${rc === "valid" || rc === "checked_in" ? "bg-emerald-950 border border-emerald-600" : rc === "already" ? "bg-amber-950 border border-amber-600" : "bg-red-950 border border-red-600"}`}>
          {rc === "checked_in" ? <CheckCircle2 className="mx-auto text-emerald-400" size={40} /> : rc === "valid" ? <CheckCircle2 className="mx-auto text-emerald-400" size={40} /> : rc === "already" ? <AlertTriangle className="mx-auto text-amber-400" size={40} /> : <XCircle className="mx-auto text-red-400" size={40} />}
          <p className="font-display text-2xl uppercase text-white mt-2">{rc === "checked_in" ? "✓ Checked In" : rc === "valid" ? "Valid Ticket" : rc === "already" ? "Already Checked In" : scanResult.message}</p>
          {scanResult.ticket && (
            <div className="mt-2 text-sm text-zinc-300">
              <p className="text-white font-semibold">{scanResult.ticket.holder_name}</p>
              <p>{scanResult.ticket.ticket_type_name} · {scanResult.ticket.order_number}</p>
              {scanResult.ticket.checked_in_at && <p className="text-xs text-zinc-500 mt-1">at {new Date(scanResult.ticket.checked_in_at).toLocaleString()}</p>}
            </div>
          )}
          {rc === "valid" && <button data-testid="confirm-checkin" onClick={() => checkIn(scanResult.ticket.id)} className="btn-gold w-full mt-4 rounded-lg py-3 font-oswald tracking-widest">CHECK IN</button>}
          {rc === "already" && <button data-testid="override-checkin" onClick={() => checkIn(scanResult.ticket.id, true)} className="w-full mt-4 rounded-lg border border-amber-600 py-3 font-oswald tracking-widest text-amber-300 text-sm">OVERRIDE & CHECK IN AGAIN</button>}
          <button onClick={() => setScanResult(null)} className="block mx-auto mt-3 text-xs text-zinc-400">Dismiss</button>
        </div>
      )}

      {/* Scanner */}
      <div className="mt-4">
        {scanning ? (
          <div>
            <div id="qr-reader" className="rounded-xl overflow-hidden border border-[#27272A]" />
            <button onClick={stopScanner} className="w-full mt-2 rounded-lg border border-[#27272A] py-2.5 text-sm text-zinc-300">Stop Camera</button>
          </div>
        ) : (
          <button data-testid="scan-qr-btn" onClick={startScanner} className="btn-gold w-full rounded-lg py-4 font-oswald tracking-widest flex items-center justify-center gap-2"><Camera size={18} /> SCAN QR CODE</button>
        )}
      </div>

      {/* Search */}
      <div className="mt-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-3.5 text-zinc-500" />
          <input data-testid="guest-search" placeholder="Search name, email, order #, ticket #" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} className="w-full rounded-lg bg-[#141417] border border-[#27272A] pl-9 pr-4 py-3 text-white text-sm outline-none focus:border-[#D4AF37]" />
        </div>
        <div className="mt-3 space-y-2">
          {results.map((t) => (
            <div key={t.id} data-testid={`guest-result-${t.id}`} className="rounded-lg border border-[#27272A] bg-[#18181B] p-3 flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-semibold">{t.holder_name}</p>
                <p className="text-xs text-zinc-400">{t.ticket_type_name} · {t.ticket_number}</p>
              </div>
              {t.status === "cancelled" ? <span className="text-xs text-red-400">Cancelled</span> :
                t.checked_in ? <span className="text-xs text-emerald-400 font-semibold">✓ In</span> :
                <button data-testid={`checkin-btn-${t.id}`} onClick={() => checkIn(t.id)} className="btn-gold rounded-md px-3 py-1.5 text-xs font-oswald tracking-widest">CHECK IN</button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
